import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import styled from "styled-components";
import {
  colorBackground,
  colorFieldsBorder,
  colorLightGray,
  colorText,
  colorTextAdditional,
} from "../../../utils/palette";
import { putApi } from "../../../api/getUser";
import { useToast } from "../../../contexts/ToastContext";
import { InsightsSettingsModal } from "./InsightsPanel";
import { AddFormButton, FilterButton } from "../../../shared/components/TableElements";

const STORAGE_KEY = "ai_insights_intro_shown";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.div`
  background: ${colorBackground};
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18);
  max-width: 560px;
  width: 100%;
  padding: 32px 36px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconTile = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7b6ad4, #5b4b9e);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
  margin-bottom: 18px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 12px;
`;

const Description = styled.p`
  font-size: 14px;
  color: ${colorTextAdditional};
  line-height: 1.6;
  margin: 0 0 20px;
`;

const CreditsBox = styled.div`
  width: 100%;
  background: #f7f8fb;
  border: 1px solid ${colorFieldsBorder};
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 13px;
  color: ${colorText};
  line-height: 1.5;
  text-align: left;
  margin-bottom: 22px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
`;

const OutlineBtn = styled.button`
  padding: 10px 22px;
  border-radius: 6px;
  border: 1px solid ${colorFieldsBorder};
  background: transparent;
  color: ${colorText};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover {
    background: ${colorLightGray};
  }
`;

const SolidBtn = styled.button`
  padding: 10px 26px;
  border-radius: 6px;
  border: none;
  background: #1b2a4a;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover {
    background: #253d6e;
  }
`;

export default function InsightsIntroPopup({ onOpenInsightsPanel }) {
  const { t } = useTranslation("common");
  const { showToast } = useToast();
  const { userId, userName } = useSelector((state) => state.user);

  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [frequency, setFrequency] = useState(1);
  const [pausedBy, setPausedBy] = useState("");
  const [pausedAt, setPausedAt] = useState(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    hasFetched.current = true;

    (async () => {
      try {
        const res = await putApi(
          { querystring: "get_ai_insight_settings" },
          "dashboard",
        );
        if (res?.success && res?.data?.length > 0) {
          const {
            frequency: freq,
            is_active,
            paused_by,
            paused_at,
            last_run_at,
          } = res.data[0];
          setIsEnabled(is_active === 1);
          setFrequency(freq);
          setPausedBy(paused_by ?? "");
          setPausedAt(paused_at || null);
          if (last_run_at) setVisible(true);
        }
      } catch (err) {
        console.error("Error fetching insights settings for intro popup:", err);
      }
    })();
  }, []);

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  }

  function handleAdjustSettings() {
    dismiss();
    // TODO: revisit — remove if intro popup should NOT also open the side panel
    onOpenInsightsPanel?.();
    setSettingsOpen(true);
  }

  async function handleSaveSettings(newEnabled, newFrequency, setLoading) {
    const nowSec = Math.floor(Date.now() / 1000);
    const apiData = {
      is_active: newEnabled ? 1 : 0,
      frequency: Number(newFrequency),
      querystring: "save_ai_insight_settings",
      paused_by: newEnabled ? "" : userId,
      paused_at: newEnabled ? null : nowSec,
    };
    try {
      setLoading(true);
      const res = await putApi(apiData, "dashboard");
      if (res.success) {
        setIsEnabled(newEnabled);
        setFrequency(newFrequency);
        if (!newEnabled) {
          setPausedBy(userName ?? "");
          setPausedAt(nowSec);
        } else {
          setPausedBy("");
          setPausedAt(null);
        }
        showToast("success", t("insights_settings_saved"));
      }
    } catch (err) {
      console.error("Error saving insights settings:", err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {visible && (
        <Backdrop>
          <Card>
            <IconTile>
              <i className="fi fi-rr-sparkles" />
            </IconTile>
            <Title>{t("ai_insights_intro_title")}</Title>
            <Description>{t("ai_insights_intro_desc")}</Description>
            <CreditsBox>
              <Trans
                t={t}
                i18nKey="ai_insights_intro_credits_hint"
                components={{ strong: <strong /> }}
              />
            </CreditsBox>
            <Footer>
              <FilterButton onClick={handleAdjustSettings}>
                {t("adjust_settings")}
              </FilterButton>
              <AddFormButton onClick={dismiss}>{t("got_it")}</AddFormButton>
            </Footer>
          </Card>
        </Backdrop>
      )}

      {settingsOpen && (
        <InsightsSettingsModal
          onClose={() => setSettingsOpen(false)}
          isEnabled={isEnabled}
          frequency={frequency}
          onSave={handleSaveSettings}
          pausedBy={pausedBy}
          pausedAt={pausedAt}
        />
      )}
    </>
  );
}
